import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CreditCard,
    Smartphone,
    Plus,
    X,
    ArrowRight,
    CheckCircle
} from 'lucide-react';

interface PaymentMethod {
    id: string;
    name: string;
    icon: React.ComponentType<any>;
    color: string;
    bgColor: string;
    description: string;
    minAmount: number;
    maxAmount: number;
}

const paymentMethods: PaymentMethod[] = [
    {
        id: 'easypaisa',
        name: 'EasyPaisa',
        icon: Smartphone,
        color: 'text-green-600',
        bgColor: 'bg-green-50 hover:bg-green-100',
        description: 'Mobile wallet payment',
        minAmount: 1000,
        maxAmount: 50000
    },
    {
        id: 'jazzcash',
        name: 'JazzCash',
        icon: Smartphone,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 hover:bg-blue-100',
        description: 'Mobile wallet payment',
        minAmount: 1000,
        maxAmount: 50000
    },
    {
        id: 'visa',
        name: 'Visa',
        icon: CreditCard,
        color: 'text-blue-700',
        bgColor: 'bg-blue-50 hover:bg-blue-100',
        description: 'Credit/Debit card',
        minAmount: 5000,
        maxAmount: 100000
    },
    {
        id: 'mastercard',
        name: 'Mastercard',
        icon: CreditCard,
        color: 'text-red-600',
        bgColor: 'bg-red-50 hover:bg-red-100',
        description: 'Credit/Debit card',
        minAmount: 5000,
        maxAmount: 100000
    }
];

interface PaymentMethodsProps {
    isOpen: boolean;
    onClose: () => void;
    onPaymentMethodSelect: (method: PaymentMethod) => void;
}

export default function PaymentMethods({ isOpen, onClose, onPaymentMethodSelect }: PaymentMethodsProps) {
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
    const [amount, setAmount] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleAmountSubmit = async () => {
        if (!selectedMethod || !amount) return;

        const amountNum = parseFloat(amount);
        if (amountNum < selectedMethod.minAmount || amountNum > selectedMethod.maxAmount) {
            alert(`Amount must be between Rs.${selectedMethod.minAmount.toLocaleString()} and Rs.${selectedMethod.maxAmount.toLocaleString()}`);
            return;
        }

        setIsProcessing(true);

        // Simulate payment processing
        setTimeout(() => {
            setIsProcessing(false);
            alert(`Payment of Rs.${amountNum.toLocaleString()} via ${selectedMethod.name} processed successfully!`);
            onClose();
            setAmount('');
            setSelectedMethod(null);
        }, 2000);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-600 to-blue-600 p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold">Add Money to Wallet</h2>
                                <p className="text-green-100 mt-1">Choose your preferred payment method</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                        {!selectedMethod ? (
                            <>
                                {/* Payment Methods Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    {paymentMethods.map((method) => {
                                        const Icon = method.icon;
                                        return (
                                            <motion.button
                                                key={method.id}
                                                onClick={() => setSelectedMethod(method)}
                                                className={`p-6 rounded-xl border-2 border-gray-200 ${method.bgColor} transition-all duration-200 hover:shadow-lg hover:scale-105`}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <div className={`p-3 rounded-lg bg-white ${method.color}`}>
                                                        <Icon className="w-8 h-8" />
                                                    </div>
                                                    <div className="text-left flex-1">
                                                        <h3 className="text-lg font-semibold text-gray-900">{method.name}</h3>
                                                        <p className="text-sm text-gray-600">{method.description}</p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Rs.{method.minAmount.toLocaleString()} - Rs.{method.maxAmount.toLocaleString()}
                                                        </p>
                                                    </div>
                                                    <ArrowRight className="w-5 h-5 text-gray-400" />
                                                </div>
                                            </motion.button>
                                        );
                                    })}
                                </div>

                                {/* Quick Add Buttons */}
                                <div className="border-t pt-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Add Amounts</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {[5000, 10000, 25000, 50000].map((amount) => (
                                            <motion.button
                                                key={amount}
                                                onClick={() => {
                                                    setAmount(amount.toString());
                                                    // Auto-select first method for quick add
                                                    setSelectedMethod(paymentMethods[0]);
                                                }}
                                                className="p-3 bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-300 rounded-lg transition-all duration-200"
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <div className="text-center">
                                                    <div className="text-lg font-semibold text-gray-900">Rs.{amount.toLocaleString()}</div>
                                                    <div className="text-xs text-gray-500">Quick Add</div>
                                                </div>
                                            </motion.button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* Payment Form */
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="space-y-6"
                            >
                                {/* Selected Method Header */}
                                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                                    <div className={`p-3 rounded-lg bg-white ${selectedMethod.color}`}>
                                        <selectedMethod.icon className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900">{selectedMethod.name}</h3>
                                        <p className="text-gray-600">{selectedMethod.description}</p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedMethod(null)}
                                        className="ml-auto p-2 hover:bg-gray-200 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>

                                {/* Amount Input */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Enter Amount
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                                            Rs.
                                        </span>
                                        <input
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder="Enter amount"
                                            className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-lg font-semibold"
                                            min={selectedMethod.minAmount}
                                            max={selectedMethod.maxAmount}
                                        />
                                    </div>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Min: Rs.{selectedMethod.minAmount.toLocaleString()} | Max: Rs.{selectedMethod.maxAmount.toLocaleString()}
                                    </p>
                                </div>

                                {/* Processing State */}
                                {isProcessing && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-center py-8"
                                    >
                                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                                        <p className="text-lg font-semibold text-gray-700">Processing Payment...</p>
                                        <p className="text-gray-500">Please don't close this window</p>
                                    </motion.div>
                                )}

                                {/* Action Buttons */}
                                {!isProcessing && (
                                    <div className="flex space-x-4">
                                        <motion.button
                                            onClick={() => setSelectedMethod(null)}
                                            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            Back
                                        </motion.button>
                                        <motion.button
                                            onClick={handleAmountSubmit}
                                            disabled={!amount || parseFloat(amount) < selectedMethod.minAmount || parseFloat(amount) > selectedMethod.maxAmount}
                                            className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <span>Add Money</span>
                                            <ArrowRight className="w-5 h-5" />
                                        </motion.button>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
